import { Component, DestroyRef, EventEmitter, inject, Inject, Input, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AzureAssetsSearchService, DropdownFacetItem, RangeFacet, SearchAsset } from '../..';
import { PageEvent } from '@angular/material/paginator';
import { MediaPreviewComponent, PreviewMode, UtilityService } from 'src/app/@shared';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface AssetModelData {
  searchPlaceholder: string;
  searchQuery: string;
  pageSize: number;
  assetsIds: string[];
  viewMode: ViewMode;
  isSingleSelect?: boolean;
}

const DEFAULT_SEARCH_PLACEHOLDER = 'Search Assets';
export enum ViewMode {
  Table = "table",
  Card = "card",
}

@Component({
  selector: 'app-assets-search-dialog',
  templateUrl: './assets-search-dialog.component.html',
  styleUrls: ['./assets-search-dialog.component.scss'],
})
export class AssetsSearchDialogComponent {
  @Input() assetsIds: string[] = [];
  @Input() searchPlaceholder: string = DEFAULT_SEARCH_PLACEHOLDER;
  @Output() onSelection: EventEmitter<string[]> = new EventEmitter();
  @Output() emitSlelectedAsset: EventEmitter<SearchAsset[]> = new EventEmitter();
  private destroyRef = inject(DestroyRef);

  modelData!: AssetModelData;
  ViewMode = ViewMode;
  PreviewMode = PreviewMode;
  defaultViewMode = ViewMode.Table;
  dataSource = new MatTableDataSource<any>([]);

  pageIndex: number = 0;
  pageLength: number = 0; // Total number of results
  @Input() pageSize: number = 50; // Default page size
  pageSizeOptions = [5, 10, 20, 50, 100, 500];

  pageEvent: PageEvent = new PageEvent;

  disabled: boolean = false;
  hidePageSize: boolean = false;
  showPageSizeOptions: boolean = true;
  showFirstLastButtons: boolean = true;

  assetTypes: DropdownFacetItem[] = [];
  selectedAssetType: DropdownFacetItem[] = [];

  colorChannels: DropdownFacetItem[] = [];
  selectedColorChannels: DropdownFacetItem[] = [];

  assetGroupsList: DropdownFacetItem[] = [];
  selectedAssetGroup: DropdownFacetItem[] = [];
  
  // fileTypes: DropdownFacetItem[] = [];
  // selectedFileTypes: DropdownFacetItem[] = [];
  
  productCategories: DropdownFacetItem[] = [];
  selectedProductCategories: DropdownFacetItem[] = [];

  rangeInvalid = false;
  assetDateRangeFacet!: RangeFacet;
  uploadStartDate: Date | null = null;
  uploadEndDate: Date | null = null;

  @Input() searchQuery: string = '';
  searchQueryVal: string = '';
  searchResults: SearchAsset[] = [];
  searchResultsCount: number = 0;
  showResults: boolean = false;
  isSelectionChanged: boolean = false;

  selection = new SelectionModel<any>(true, [], true, (a1, a2) => a1.assetId === a2.assetId);
  displayedColumns = ['select', 'Image', 'AssetName', 'Type', 'Group', 'DateAdded', 'PreviewImage'];
  flexMediaWatcher!: Subscription;

  constructor(
    private dialog: MatDialog,
    private mediaObserver: MediaObserver,
    public utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA) public data: AssetModelData,
    private azureAssetsSearchService: AzureAssetsSearchService,
    private dialogRef: MatDialogRef<AssetsSearchDialogComponent>,
    private matSnackBar: MatSnackBar
  ) {
    this.modelData = data;
    if(this.modelData.isSingleSelect) {
      this.selection = new SelectionModel<any>(false, [], true, (a1, a2) => a1.assetId === a2.assetId);
    }
  }

  async ngOnInit() {
    this.azureAssetsSearchService.clearFilters();
    this.azureAssetsSearchService.reset();
    this.detectViewportSizeChange();
    this.updateModalData();

    this.azureAssetsSearchService.updatePageSize(this.pageSize);
    await this.azureAssetsSearchService.init();
    this.azureAssetsSearchService.search(this.searchQuery ?? '');

    this.azureAssetsSearchService.assets$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.searchResults = res;
        this.dataSource.data = res;
        this.restoreSelection(res);
      });

    combineLatest([
      this.azureAssetsSearchService.searchStateParams$,
      this.azureAssetsSearchService.totalAssetsCount$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, total]) => {
        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;
        this.pageLength = total;
        this.searchResultsCount = total;
      });

    this.azureAssetsSearchService.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.showResults = !res);

    this.azureAssetsSearchService.assetTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.assetTypes = res);

    this.azureAssetsSearchService.colorChannels$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.colorChannels = res);

    this.azureAssetsSearchService.productCategories$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.productCategories = res);

    this.azureAssetsSearchService.assetGroups$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.assetGroupsList = res);


    this.selection.changed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(changeObj => {
        const selectedIds = changeObj.source.selected.map(a => a.assetId);
        this.isSelectionChanged =
          selectedIds.length > 0 &&
          !this.areAssetIdsEqual(selectedIds, this.assetsIds);
      });
  }

  ngOnDestroy(): void {
    this.azureAssetsSearchService.reset();
    this.flexMediaWatcher?.unsubscribe();
  }

  private updateModalData() {
    this.pageSize = this.modelData.pageSize ?? this.pageSize;
    this.searchQuery = this.modelData.searchQuery ?? this.searchQuery;
    this.searchPlaceholder = this.modelData.searchPlaceholder ?? this.searchPlaceholder;

    if (this.modelData.viewMode === ViewMode.Card || this.modelData.viewMode === ViewMode.Table) {
      this.defaultViewMode = this.modelData.viewMode;
    }
    this.assetsIds = Array.isArray(this.modelData.assetsIds) ? this.modelData.assetsIds : [];
  }

  private restoreSelection(results: any[]) {
    if (!this.assetsIds?.length) return;

    const selectedAssets = results.filter(asset =>
      this.assetsIds.includes(asset.assetId)
    );

    this.selection.clear();
    this.selection.select(...selectedAssets);
  }

  detectViewportSizeChange() {
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Image', 'AssetName'];
        } else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'Image', 'AssetName', 'Type', 'Group', 'DateAdded', 'PreviewImage'];
        } else {
          this.displayedColumns = ['select', 'Image', 'AssetName', 'Type', 'Group', 'DateAdded', 'PreviewImage'];
        }
      });
  }

  onFiltersReset() {
    this.selectedAssetType = [];
    this.selectedColorChannels = [];
    this.selectedAssetGroup = [];
    this.selectedProductCategories = [];
    // this.selectedFileTypes = [];
    this.uploadStartDate = null;
    this.uploadEndDate = null;

    this.onFiltersApply();
  }

  onFiltersApply() {
    let filters = {
      assetType: this.selectedAssetType,
      assetGroup: this.selectedAssetGroup,
      colorChannel: this.selectedColorChannels,
      productCategory: this.selectedProductCategories,
      // fileType: this.selectedFileTypes,
      startDate: this.formattedDatetoEpoch(this.uploadStartDate),
      endDate: this.formattedDatetoEpoch(this.uploadEndDate)
    }

    if (!filters.startDate) this.uploadStartDate = null;
    if (!filters.endDate) this.uploadEndDate = null;

    this.rangeInvalid = false;

    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      this.rangeInvalid = true;
      return
    }

    // this.azureAssetsSearchService.updateSelectedItems('fileTypes', this.selectedFileTypes);
    this.azureAssetsSearchService.updateSelectedItems('assetTypes', this.selectedAssetType);
    this.azureAssetsSearchService.updateSelectedItems('assetGroups', this.selectedAssetGroup);
    this.azureAssetsSearchService.updateSelectedItems('colorChannels', this.selectedColorChannels);
    this.azureAssetsSearchService.updateSelectedItems('productCategories', this.selectedProductCategories);
    this.azureAssetsSearchService.updateDateRange('dateRange', filters.startDate, filters.endDate);
    this.onSearch();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  toggleView(mode: ViewMode) {
    this.defaultViewMode = mode;
  }

  compareWithIdFn = (o1: any, o2: any) => {
    return o1 && o2 ? o1.value === o2.value : o1 === o2;
  };

  formattedDatetoEpoch(dateObj: Date | null): number | null {
    if (!dateObj) return null;
    const dateString = new Date(dateObj).toLocaleDateString();
    const [month, day, year] = dateString.split('/').map(Number);
    // Format the date as "YYYY-MM-DDZ00:00:00"
    const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}Z00:00:00`;
    var epochDate = new Date(formattedDate);
    var epochVal = epochDate.getTime() / 1000.0;

    return epochVal > 0 ? epochVal : null;
  }

  areAssetIdsEqual(selectedAssetsIds: string[], assetsIds: string[]): boolean {
    if (selectedAssetsIds.length !== assetsIds.length) {
      return false;
    }

    const sortedSelectedIds = [...selectedAssetsIds].sort();
    const sortedAssetsIds = [...assetsIds].sort();

    return sortedSelectedIds.every((id, index) => id === sortedAssetsIds[index]);
  }

  onSelect() {
    const assetIds: string[] = this.selection.selected.map(asset => asset.assetId);
    const filteredAssetsIds = assetIds.filter(id => !this.assetsIds.includes(id));
    if (!filteredAssetsIds.length) {
      this.matSnackBar.open('No new asset(s) selected.', 'Close', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
      return;
    }
    this.onSelection.emit(filteredAssetsIds);
    this.emitSlelectedAsset.emit(this.selection.selected);
    this.closeDialog();
  }

  onSearch(): void {
    const searchQuery = this.searchQuery.trim();

    if (typeof searchQuery === "string") {
      this.searchQueryVal = searchQuery;
      this.azureAssetsSearchService.search(searchQuery);
      this.resetSearchValues();
    }
  }

  clearSearch(searchInput: HTMLInputElement | null): void {
    searchInput && (searchInput.value = '');
    this.searchQuery = '';
    this.searchQueryVal = '';
    this.azureAssetsSearchService.search('');
    this.resetSearchValues();
  }

  onPageChange(event: PageEvent): void {
    this.pageEvent = event;
    this.azureAssetsSearchService.pageChange(event.pageIndex, event.pageSize);
    this.resetSearchValues();
  }

  resetSearchValues() {
    this.searchResults = [];
    this.searchResultsCount = 0;
  }

  onSort(sortState: Sort): void {
    let orderby = '';
    switch (sortState.active) {
      case 'AssetName':
        orderby = 'assetName';
        break;
      case 'DateAdded':
        orderby = 'dateAddedEPOCH';
        break;
      case 'Group':
        orderby = 'assetGroupName';
        break;
      case 'Type':
        orderby = 'fileType';
        break;
      default:
        orderby = 'dateAddedEPOCH';
        break;
    }

    this.azureAssetsSearchService.sort(orderby, sortState.direction as 'asc' | 'desc');
  }

  isDisabled(asset: SearchAsset): boolean {
    return this.assetsIds.includes(asset.assetId) || asset.doNotUse ? true : false
  }

  toggleSelect(checked: boolean, asset: any) {
    if (checked) {
      if (!this.selection.isSelected(asset)) {
        this.selection.select(asset);
      }
    } else {
      this.selection.deselect(asset);
    }
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.searchResults.forEach(asset => {
        if (!this.selection.isSelected(asset) && !this.assetsIds.includes(asset.assetId) && !asset.doNotUse) {
          this.selection.select(asset);
        }
      });
    } else {
      this.searchResults.forEach(asset => {
        !this.assetsIds.includes(asset.assetId) && this.selection.deselect(asset);
      });
    }
  }

  isAllSelectedOnPage(): boolean {
    return this.searchResults.every(asset => this.selection.isSelected(asset));
  }

  isSomeSelectedOnPage(): boolean {
    return this.searchResults.some(asset => this.selection.isSelected(asset));
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png';
  }

  previewAssetImage(assetsDetails: any) {
    let urlToPass = '';
    let videoCaptionsUrl = '';
    if (assetsDetails && assetsDetails.FileType?.includes('x-zip') && assetsDetails.ZipConfig && assetsDetails.ZipType && assetsDetails.ZipType.includes('Video')) {
      let zipConfigInfo = JSON.parse(assetsDetails.ZipConfig);
      urlToPass = zipConfigInfo.PrimaryFileUrl;
      videoCaptionsUrl = zipConfigInfo.CaptionsFileUrl;
    } else if (assetsDetails.FileType?.includes('x-zip') && !assetsDetails?.ZipType?.includes('Video')) {
      urlToPass = '../../../../assets/images/mp4.png'
    }
    else {
      urlToPass = assetsDetails.webUrl as string;
    }
    const dialogRef = this.dialog.open(MediaPreviewComponent, {
      //data: { name: this.assetsDetails.AssetName, url: this.assetsDetails.FileName, type: this.assetsDetails.FileType, assetUrl: this.utilityService.getFilePreviewUrl(this.currentAsset, PreviewMode.Full) },
      data: { name: assetsDetails.assetName, url: assetsDetails.fileName, type: assetsDetails.fileType, assetUrl: urlToPass, videoCaptionsUrl: videoCaptionsUrl },
    });
  }

}
